import React, { useState, useEffect } from 'react';
import { ProcessoComercial } from '../ComercialDashboard';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../integrations/supabase/client';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { X, Save } from 'lucide-react';

interface ProcessoFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  processo?: ProcessoComercial | null;
  tipo: 'venda' | 'locacao' | 'prestacao_servico';
}

interface Property {
  listing_id: string;
  title: string;
  address: string;
  property_type: string;
  transaction_type: string;
}

interface Template {
  id: string;
  nome: string;
  tipo: string;
}

interface Formulario {
  id: string;
  nome: string;
  tipo: string;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export const ProcessoForm: React.FC<ProcessoFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  processo,
  tipo
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [, setFormularios] = useState<Formulario[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const [formData, setFormData] = useState({
    titulo: '',
    unidade: '',
    property_id: '',
    responsavel_id: '',
    template_contrato_id: '',
    observacoes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadData();
      if (processo) {
        setFormData({
          titulo: processo.titulo || '',
          unidade: processo.unidade || '',
          property_id: processo.property_id || '',
          responsavel_id: processo.responsavel_id || '',
          template_contrato_id: processo.template_contrato_id || '',
          observacoes: processo.observacoes || ''
        });
      } else {
        setFormData({
          titulo: '',
          unidade: '',
          property_id: '',
          responsavel_id: user?.id || '',
          template_contrato_id: '',
          observacoes: ''
        });
      }
    }
  }, [isOpen, processo, user]);

  const loadData = async () => {
    try {
      // Load properties
      const { data: propertiesData } = await supabase
        .from('properties')
        .select('listing_id, title, address, property_type, transaction_type')
        .eq('is_active', true)
        .order('title');

      setProperties(propertiesData || []);

      // Load templates
      const { data: templatesData } = await supabase
        .from('templates_contrato')
        .select('id, nome, tipo')
        .eq('tipo', tipo)
        .eq('ativo', true)
        .order('nome');

      setTemplates(templatesData || []);

      // Load formularios
      const { data: formulariosData } = await supabase
        .from('formularios_processo')
        .select('id, nome, tipo')
        .eq('tipo', tipo)
        .eq('ativo', true)
        .order('nome');

      setFormularios(formulariosData || []);

      // Load users
      const { data: usersData } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, email')
        .eq('is_active', true)
        .order('first_name');

      setUsers(usersData?.map(u => ({
        id: u.user_id || '',
        first_name: u.first_name,
        last_name: u.last_name,
        email: u.email
      })) || []);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.titulo.trim()) {
      toast({
        title: "Erro",
        description: "O título é obrigatório",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      const processData = {
        tipo,
        titulo: formData.titulo,
        unidade: formData.unidade || null,
        property_id: formData.property_id || null,
        responsavel_id: formData.responsavel_id || user?.id,
        template_contrato_id: formData.template_contrato_id || null,
        observacoes: formData.observacoes || null,
        status: 'criado'
      };

      if (processo) {
        // Update existing process
        const { error } = await supabase
          .from('processos_comerciais')
          .update(processData)
          .eq('id', processo.id);

        if (error) throw error;

        // Add to history
        await supabase.from('historico_processo').insert({
          processo_id: processo.id,
          acao: 'processo_atualizado',
          descricao: `Processo atualizado: ${formData.titulo}`,
          usuario_id: user?.id
        });

      } else {
        // Create new process
        const { data: newProcess, error } = await supabase
          .from('processos_comerciais')
          .insert(processData)
          .select()
          .single();

        if (error) throw error;

        // Add to history
        await supabase.from('historico_processo').insert({
          processo_id: newProcess.id,
          acao: 'processo_criado',
          descricao: `Processo de ${tipo} criado: ${formData.titulo}`,
          usuario_id: user?.id
        });
      }

      onSuccess();
    } catch (error) {
      console.error('Erro ao salvar processo:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar o processo",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {processo ? 'Editar' : 'Criar'} Processo de {tipo === 'venda' ? 'Venda' : tipo === 'locacao' ? 'Locação' : 'Prestação de Serviço'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Título */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Título do Processo *
            </label>
            <input
              type="text"
              required
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              placeholder={`Ex: ${tipo === 'venda' ? 'Venda' : tipo === 'locacao' ? 'Locação' : 'Prestação de Serviço'} Apartamento Centro`}
            />
          </div>

          {/* Unidade */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Unidade
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
              value={formData.unidade}
              onChange={(e) => setFormData({ ...formData, unidade: e.target.value })}
              placeholder="Ex: Apto 101, Casa 5, Loja 3"
            />
          </div>

          {/* Imóvel */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Imóvel
            </label>
            <select
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
              value={formData.property_id}
              onChange={(e) => setFormData({ ...formData, property_id: e.target.value })}
            >
              <option value="">Selecione um imóvel (opcional)</option>
              {properties.map((property) => (
                <option key={property.listing_id} value={property.listing_id}>
                  {property.listing_id} - {property.title} - {property.address}
                </option>
              ))}
            </select>
          </div>

          {/* Responsável */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Responsável
            </label>
            <select
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
              value={formData.responsavel_id}
              onChange={(e) => setFormData({ ...formData, responsavel_id: e.target.value })}
            >
              <option value="">Selecione um responsável</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.email})
                </option>
              ))}
            </select>
          </div>

          {/* Template de Contrato */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Template de Contrato
            </label>
            <select
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
              value={formData.template_contrato_id}
              onChange={(e) => setFormData({ ...formData, template_contrato_id: e.target.value })}
            >
              <option value="">Selecione um template (opcional)</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.nome}
                </option>
              ))}
            </select>
          </div>

          {/* Observações */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Observações
            </label>
            <textarea
              rows={4}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              placeholder="Observações sobre o processo..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};